import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { LoadingScreen } from './common/LoadingScreen';

type ProtectedRouteProps = {
  children: ReactNode;
  requiredModule?: string;
  requiredAction?: string;
};

/**
 * ProtectedRoute component checks if a user is authenticated and has the required permissions
 * to access a route. If not, it redirects to the login page or shows an unauthorized message.
 */
export default function ProtectedRoute({ 
  children, 
  requiredModule,
  requiredAction = 'view'
}: ProtectedRouteProps) {
  const location = useLocation();
  const { user, loading: authLoading } = useAuthContext();
  const { 
    hasPermission, 
    loading: permissionsLoading, 
    isAdmin,
    userRoles
  } = usePermissions();

  // Show a consistent loading screen while either auth or permissions are loading.
  if (authLoading || permissionsLoading) {
    return <LoadingScreen message="Verificando permissões..." size="x-small" />;
  }
  
  // After loading, if there's no user, redirect to login.
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // After loading, if the user has no roles and is not an admin, deny access.
  if (!isAdmin && userRoles.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh]">
        <h1 className="text-2xl font-extrabold text-red-600 mb-4">Acesso Negado</h1>
        <p className="text-gray-600 mb-6">
          Você não possui permissões de acesso.
        </p>
        <p className="text-sm text-gray-500">
          Por favor, contate um administrador para liberar seu acesso.
        </p>
      </div>
    );
  }

  // If a required module is specified, check for specific permissions.
  if (requiredModule && !isAdmin) {
    const hasAccess = hasPermission(requiredModule, requiredAction);
    
    if (!hasAccess) {
      // User doesn't have the required permission for this specific module.
      return (
        <div className="flex flex-col items-center justify-center min-h-[70vh]">
          <h1 className="text-2xl font-extrabold text-red-600 mb-4">Acesso não autorizado</h1>
          <p className="text-gray-600 mb-6">
            Você não tem permissão para acessar esta página.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Módulo requerido: <strong>{requiredModule}</strong> | Ação: <strong>{requiredAction}</strong>
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Voltar
          </button>
        </div>
      );
    }
  }
  
  // If all checks pass, render the requested component.
  return <>{children}</>;
}